<script setup>
import { ref, onMounted } from 'vue'
import { api } from '../api'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AdminNav from '../components/AdminNav.vue'
const router = useRouter()
const { t } = useI18n()
const members = ref([])
const loading = ref(true)
const error = ref('')
const deleting = ref(null)
const dragging = ref(null)

async function load() {
  loading.value = true
  error.value = ''
  try {
    const { data } = await api.get('/members')
    members.value = data
  } catch {
    error.value = 'load'
  } finally {
    loading.value = false
  }
}

function newMember() {
  router.push('/admin/members/new')
}
function edit(m) {
  router.push('/admin/members/' + m.id)
}

async function remove(m) {
  if (!confirm(t('admin.confirmDeleteMember'))) return
  deleting.value = m.id
  try {
    await api.delete('/members/' + m.id)
    members.value = members.value.filter((x) => x.id !== m.id)
  } finally {
    deleting.value = null
  }
}

function dragStart(i) {
  dragging.value = i
}
function dragEnter(i) {
  if (dragging.value === null || dragging.value === i) return
  const arr = [...members.value]
  const [m] = arr.splice(dragging.value, 1)
  arr.splice(i, 0, m)
  members.value = arr
  dragging.value = i
}
async function dragEnd() {
  if (dragging.value === null) return
  dragging.value = null
  try {
    await api.post('/members/reorder', { order: members.value.map((m) => m.id) })
  } catch {}
}

onMounted(load)
</script>
<template>
  <section>
    <h2>{{ t('nav.members') }}</h2>
    <AdminNav />
    <div class="action-bar">
      <button class="btn-primary" @click="newMember">+ {{ t('admin.memberNew') }}</button>
      <button class="btn" @click="load" :disabled="loading">{{ t('common.refresh') }}</button>
    </div>
    <p v-if="loading">{{ t('common.loading') }}</p>
    <p v-else-if="error" class="err">{{ t('common.errorLoading') }}</p>
    <div v-else>
      <div class="table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th style="width: 80px">{{ t('admin.order') }}</th>
              <th>{{ t('common.name') }}</th>
              <th>{{ t('common.role') }}</th>
              <th>{{ t('admin.slug') }}</th>
              <th style="width: 120px"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(m, i) in members"
              :key="m.id"
              draggable="true"
              @dragstart="() => dragStart(i)"
              @dragenter.prevent="() => dragEnter(i)"
              @dragend="dragEnd"
            >
              <td class="drag-handle" :aria-label="t('admin.dragToReorder')">☰ {{ i }}</td>
              <td>
                <router-link :to="'/admin/members/' + m.id">{{ m.name }}</router-link>
              </td>
              <td>{{ m.role }}</td>
              <td>{{ m.slug }}</td>
              <td class="cell-actions">
                <button class="btn" @click="edit(m)">{{ t('common.edit') }}</button>
                <button class="btn btn-danger" :disabled="deleting === m.id" @click="remove(m)">
                  {{ t('common.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="hint">{{ t('admin.reorderMembersHint') }}</p>
    </div>
  </section>
</template>
<style scoped>
.err {
  color: #c00;
}
</style>
